/**
 * Query keys. Everything branch-scoped lives under `['branch', branchId, <area>, …]` so a
 * realtime hint (or a reconnect) can invalidate one area or the whole branch by prefix.
 */
export const qk = {
  me: ['me'] as const,
  tenantBranches: (tenantId: string) => ['tenant', tenantId, 'branches'] as const,
  staff: (tenantId: string) => ['tenant', tenantId, 'staff'] as const,
  branding: (tenantId: string) => ['tenant', tenantId, 'branding'] as const,
  /** The signed-in user's inbox in this tenant (ADR-0046). */
  notifications: (tenantId: string) => ['tenant', tenantId, 'notifications'] as const,
  /** Inventory items are tenant master data (shared by every branch). */
  inventoryItems: (tenantId: string) => ['tenant', tenantId, 'inventory-items'] as const,
  suppliers: (tenantId: string) => ['tenant', tenantId, 'suppliers'] as const,
  approvalPolicies: (tenantId: string) => ['tenant', tenantId, 'approval-policies'] as const,
  apSettings: (tenantId: string) => ['tenant', tenantId, 'ap-settings'] as const,
  approvalDelegates: (tenantId: string) => ['tenant', tenantId, 'approval-delegates'] as const,
  approvalDelegations: (tenantId: string) => ['tenant', tenantId, 'approval-delegations'] as const,

  /** Public guest QR pages, keyed by the table token (no staff session involved). */
  guestMenu: (token: string) => ['guest', token, 'menu'] as const,
  guestBill: (token: string) => ['guest', token, 'bill'] as const,
  guestOrder: (token: string) => ['guest', token, 'order'] as const,
  guestPayment: (token: string, id: string) => ['guest', token, 'payments', id] as const,
  simulatorCheckout: (reference: string) => ['payment-simulator', reference] as const,

  branch: (b: string) => ['branch', b] as const,
  branchDetail: (b: string) => ['branch', b, 'detail'] as const,
  floorPlan: (b: string) => ['branch', b, 'floor-plan'] as const,
  tableInsights: (b: string, tableId: string, from: string, to: string) =>
    ['branch', b, 'floor-plan', 'table', tableId, 'insights', from, to] as const,
  tableQrCodes: (b: string) => ['branch', b, 'floor-plan', 'qr-codes'] as const,
  promotions: (b: string) => ['branch', b, 'promotions'] as const,
  priceLists: (b: string) => ['branch', b, 'catalog', 'price-lists'] as const,
  effectivePrices: (b: string) => ['branch', b, 'catalog', 'effective-prices'] as const,
  guestContacts: (b: string) => ['branch', b, 'guest-contacts'] as const,
  catalog: (b: string) => ['branch', b, 'catalog'] as const,
  sessions: (b: string) => ['branch', b, 'table-sessions'] as const,
  session: (b: string, id: string) => ['branch', b, 'table-sessions', id] as const,
  orders: (b: string) => ['branch', b, 'orders'] as const,
  orderList: (b: string, status?: 'open' | 'closed') =>
    ['branch', b, 'orders', 'list', status ?? 'all'] as const,
  order: (b: string, id: string) => ['branch', b, 'orders', 'detail', id] as const,
  tickets: (b: string) => ['branch', b, 'tickets'] as const,
  ticketList: (b: string, scope: 'active' | 'recent', stationId?: string, orderId?: string) =>
    ['branch', b, 'tickets', scope, stationId ?? 'all', orderId ?? 'any'] as const,
  bills: (b: string) => ['branch', b, 'bills'] as const,
  billList: (b: string, status?: 'open' | 'settled') =>
    ['branch', b, 'bills', 'list', status ?? 'all'] as const,
  bill: (b: string, id: string) => ['branch', b, 'bills', 'detail', id] as const,
  cashShifts: (b: string) => ['branch', b, 'cash-shifts'] as const,
  currentShift: (b: string) => ['branch', b, 'cash-shifts', 'current'] as const,
  shiftList: (b: string) => ['branch', b, 'cash-shifts', 'list'] as const,
  reports: (b: string) => ['branch', b, 'reports'] as const,
  salesReport: (b: string, from: string, to: string) =>
    ['branch', b, 'reports', 'sales', from, to] as const,
  inventory: (b: string) => ['branch', b, 'inventory'] as const,
  stock: (b: string, locationId?: string) =>
    ['branch', b, 'inventory', 'stock', locationId ?? 'all'] as const,
  stockLocations: (b: string) => ['branch', b, 'inventory', 'locations'] as const,
  replenishment: (b: string) => ['branch', b, 'inventory', 'replenishment'] as const,
  // Warehouse (ADR-0037): under `inventory`, so `stock.changed` hints refresh them too.
  warehouseLayout: (b: string, locationId: string) =>
    ['branch', b, 'inventory', 'warehouse', 'layout', locationId] as const,
  expectedReceipts: (b: string) => ['branch', b, 'inventory', 'warehouse', 'expected'] as const,
  warehouseReceipts: (b: string) => ['branch', b, 'inventory', 'warehouse', 'receipts'] as const,
  warehouseReceipt: (b: string, id: string) =>
    ['branch', b, 'inventory', 'warehouse', 'receipt', id] as const,
  receiptInspections: (b: string, id: string) =>
    ['branch', b, 'inventory', 'warehouse', 'inspections', id] as const,
  inspectionTemplates: (b: string) =>
    ['branch', b, 'inventory', 'warehouse', 'inspection-templates'] as const,
  warehouseExceptions: (b: string, status?: string) =>
    ['branch', b, 'inventory', 'warehouse', 'exceptions', status ?? 'all'] as const,
  quarantine: (b: string) => ['branch', b, 'inventory', 'warehouse', 'quarantine'] as const,
  warehouseTasks: (b: string, filters: Readonly<Record<string, string>> = {}) =>
    ['branch', b, 'inventory', 'warehouse', 'tasks', filters] as const,
  requisitions: (b: string, filters: Readonly<Record<string, string>> = {}) =>
    ['branch', b, 'inventory', 'warehouse', 'requisitions', filters] as const,
  requisition: (b: string, id: string) =>
    ['branch', b, 'inventory', 'warehouse', 'requisition', id] as const,
  binStock: (b: string, locationId: string, binId?: string) =>
    ['branch', b, 'inventory', 'warehouse', 'bin-stock', locationId, binId ?? 'all'] as const,
  stockMovements: (b: string, filters: Readonly<Record<string, string>> = {}) =>
    ['branch', b, 'inventory', 'movements', filters] as const,
  valuation: (b: string, asOf?: string) =>
    ['branch', b, 'inventory', 'valuation', asOf ?? 'now'] as const,
  valuationHistory: (b: string, from: string, to: string) =>
    ['branch', b, 'inventory', 'valuation-history', from, to] as const,
  lots: (b: string, filters: Readonly<Record<string, string>> = {}) =>
    ['branch', b, 'inventory', 'lots', filters] as const,
  lot: (b: string, id: string) => ['branch', b, 'inventory', 'lot', id] as const,
  expiryAlerts: (b: string, horizonDays: number) =>
    ['branch', b, 'inventory', 'expiry-alerts', horizonDays] as const,
  wasteList: (b: string, filters: Readonly<Record<string, string>> = {}) =>
    ['branch', b, 'inventory', 'waste', filters] as const,
  wasteRecord: (b: string, id: string) => ['branch', b, 'inventory', 'waste-record', id] as const,
  wasteReport: (b: string, from: string, to: string) =>
    ['branch', b, 'inventory', 'waste-report', from, to] as const,
  stockCounts: (b: string, status?: string) =>
    ['branch', b, 'inventory', 'counts', status ?? 'all'] as const,
  stockCount: (b: string, id: string) => ['branch', b, 'inventory', 'count', id] as const,
  procurement: (b: string) => ['branch', b, 'procurement'] as const,
  purchaseOrders: (b: string, filters: Readonly<Record<string, string>> = {}) =>
    ['branch', b, 'procurement', 'purchase-orders', filters] as const,
  purchaseOrder: (b: string, id: string) =>
    ['branch', b, 'procurement', 'purchase-order', id] as const,
  goodsReceipts: (b: string, filters: Readonly<Record<string, string>> = {}) =>
    ['branch', b, 'procurement', 'goods-receipts', filters] as const,
  goodsReceipt: (b: string, id: string) =>
    ['branch', b, 'procurement', 'goods-receipt', id] as const,
  purchaseRequests: (b: string, filters: Readonly<Record<string, string>> = {}) =>
    ['branch', b, 'procurement', 'purchase-requests', filters] as const,
  purchaseRequest: (b: string, id: string) =>
    ['branch', b, 'procurement', 'purchase-request', id] as const,
  rfqs: (b: string) => ['branch', b, 'procurement', 'rfqs'] as const,
  rfq: (b: string, id: string) => ['branch', b, 'procurement', 'rfq', id] as const,
  supplierItems: (b: string) => ['branch', b, 'procurement', 'supplier-items'] as const,
  commercialTerms: (b: string) => ['branch', b, 'procurement', 'commercial-terms'] as const,
  approvals: (b: string) => ['branch', b, 'approvals'] as const,
  approvalInbox: (b: string) => ['branch', b, 'approvals', 'inbox'] as const,
  approvalList: (b: string, filters: Readonly<Record<string, string>> = {}) =>
    ['branch', b, 'approvals', 'list', filters] as const,
  approval: (b: string, id: string) => ['branch', b, 'approvals', 'detail', id] as const,
  budgets: (b: string, period?: string) => ['branch', b, 'budgets', period ?? 'all'] as const,
  budgetEntries: (b: string, id: string) => ['branch', b, 'budgets', 'entries', id] as const,
  approvalAssignable: (b: string, id: string) =>
    ['branch', b, 'approvals', 'assignable', id] as const,
  // Accounts payable (P1.6)
  finance: (b: string) => ['branch', b, 'finance'] as const,
  invoices: (b: string, filters: Readonly<Record<string, string>> = {}) =>
    ['branch', b, 'finance', 'invoices', filters] as const,
  invoice: (b: string, id: string) => ['branch', b, 'finance', 'invoice', id] as const,
  creditNotes: (b: string, filters: Readonly<Record<string, string>> = {}) =>
    ['branch', b, 'finance', 'credit-notes', filters] as const,
  apPayments: (b: string, filters: Readonly<Record<string, string>> = {}) =>
    ['branch', b, 'finance', 'payments', filters] as const,
  apAging: (b: string, asOf: string) => ['branch', b, 'finance', 'aging', asOf] as const,
  // Transfers, consumption, recalls, reconciliation (inventory area)
  transfers: (b: string, filters: Readonly<Record<string, string>> = {}) =>
    ['branch', b, 'inventory', 'transfers', filters] as const,
  transfer: (b: string, id: string) => ['branch', b, 'inventory', 'transfer', id] as const,
  consumption: (b: string, from: string, to: string) =>
    ['branch', b, 'inventory', 'consumption', from, to] as const,
  recallSearch: (b: string, filters: Readonly<Record<string, string>>) =>
    ['branch', b, 'inventory', 'recall-search', filters] as const,
  recallCases: (b: string) => ['branch', b, 'inventory', 'recalls'] as const,
  recallCase: (b: string, id: string) => ['branch', b, 'inventory', 'recall', id] as const,
  reconciliation: (b: string) => ['branch', b, 'inventory', 'reconciliation'] as const,
  countAccuracy: (b: string, from: string, to: string) =>
    ['branch', b, 'inventory', 'count-accuracy', from, to] as const,
  // Recipes (catalog area: menu changes invalidate them too)
  recipes: (b: string) => ['branch', b, 'catalog', 'recipes'] as const,
  // Supplier analytics (procurement area)
  supplierScorecard: (b: string, supplierId: string, window: string) =>
    ['branch', b, 'procurement', 'scorecard', supplierId, window] as const,
  supplierPriceHistory: (
    b: string,
    supplierId: string,
    filters: Readonly<Record<string, string>>,
  ) => ['branch', b, 'procurement', 'price-history', supplierId, filters] as const,
  supplierCompare: (b: string, filters: Readonly<Record<string, string>>) =>
    ['branch', b, 'procurement', 'supplier-compare', filters] as const,
};

type Area =
  | 'detail'
  | 'floor-plan'
  | 'catalog'
  | 'table-sessions'
  | 'orders'
  | 'tickets'
  | 'bills'
  | 'cash-shifts'
  | 'reports'
  | 'inventory'
  | 'procurement'
  | 'promotions'
  | 'approvals'
  | 'budgets'
  | 'finance';

/** Which areas a realtime `event_type` touches. Unknown types touch nothing. */
export function areasForEvent(eventType: string): Area[] {
  if (eventType === 'catalog.updated') return ['catalog'];
  if (eventType === 'promotion.updated') return ['promotions'];
  if (eventType === 'cash_shift.updated') return ['cash-shifts'];
  if (eventType.startsWith('branch.')) return ['detail'];
  // Waste and count decisions also appear in the approval inbox.
  if (eventType.startsWith('stock.')) return ['inventory', 'approvals'];
  // Approval decisions move documents (PR/PO status) and budget commitments.
  if (eventType.startsWith('purchase_order.') || eventType.startsWith('purchase_request.')) {
    return ['procurement', 'approvals', 'budgets'];
  }
  if (eventType.startsWith('approval.')) return ['approvals', 'procurement', 'budgets'];
  if (eventType.startsWith('budget.')) return ['budgets'];
  // Invoices and payments move AP balances, PO billing and budget actuals.
  if (eventType.startsWith('ap.')) return ['finance', 'approvals', 'budgets', 'procurement'];
  if (eventType.startsWith('goods_receipt.')) return ['procurement', 'inventory'];
  // Putaway tasks and bin stock (ADR-0038) live under `inventory`.
  if (eventType.startsWith('warehouse.')) return ['inventory'];
  if (eventType.startsWith('order.')) return ['orders', 'floor-plan', 'table-sessions'];
  if (eventType.startsWith('kitchen.ticket.')) return ['tickets', 'orders'];
  if (eventType.startsWith('table.')) return ['floor-plan', 'table-sessions', 'orders'];
  if (eventType.startsWith('bill.')) return ['bills', 'floor-plan', 'table-sessions'];
  if (eventType.startsWith('payment.'))
    return ['bills', 'cash-shifts', 'floor-plan', 'table-sessions', 'reports'];
  return [];
}

export function keysForEvent(branchId: string, eventType: string): (readonly string[])[] {
  return areasForEvent(eventType).map((area) => ['branch', branchId, area]);
}
